import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    ClickAwayListener,
    Popper,
    Skeleton,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useMarketRaw } from '../api/useMarketRaw';

const OPEN_DELAY_MS = 250;
const CLOSE_DELAY_MS = 220;
const COPIED_RESET_MS = 1400;

let zCounter = 0;

export interface RawDataPopoverProps {
    marketId: string;
}

export function RawDataPopover({ marketId }: RawDataPopoverProps) {
    const theme = useTheme();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [pinned, setPinned] = useState(false);
    const [copied, setCopied] = useState(false);
    const [depth, setDepth] = useState(0);
    const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { data, loading, error } = useMarketRaw(anchor ? marketId : null);

    const clearTimers = useCallback(() => {
        if (openTimer.current) clearTimeout(openTimer.current);
        if (closeTimer.current) clearTimeout(closeTimer.current);
        openTimer.current = null;
        closeTimer.current = null;
    }, []);

    const bringToFront = useCallback(() => {
        zCounter += 1;
        setDepth(zCounter);
    }, []);

    useEffect(
        () => () => {
            if (openTimer.current) clearTimeout(openTimer.current);
            if (closeTimer.current) clearTimeout(closeTimer.current);
            if (copyTimer.current) clearTimeout(copyTimer.current);
        },
        [],
    );

    const close = useCallback(() => {
        clearTimers();
        setPinned(false);
        setAnchor(null);
    }, [clearTimers]);

    const scheduleClose = useCallback(() => {
        if (pinned) return;
        clearTimers();
        closeTimer.current = setTimeout(() => setAnchor(null), CLOSE_DELAY_MS);
    }, [pinned, clearTimers]);

    const rawText = data ? JSON.stringify(data.raw_data, null, 2) : '';

    const copy = useCallback(() => {
        if (!rawText) return;
        void navigator.clipboard.writeText(rawText).then(() => {
            setCopied(true);
            if (copyTimer.current) clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
        });
    }, [rawText]);

    return (
        <>
            <Tooltip title="Raw market data" disableInteractive enterDelay={600}>
                <Box
                    onMouseEnter={e => {
                        const target = e.currentTarget;
                        clearTimers();
                        openTimer.current = setTimeout(() => {
                            bringToFront();
                            setAnchor(target);
                        }, OPEN_DELAY_MS);
                    }}
                    onMouseLeave={scheduleClose}
                    onClick={e => {
                        e.stopPropagation();
                        clearTimers();
                        bringToFront();
                        setAnchor(e.currentTarget);
                        setPinned(true);
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        borderRadius: '3px',
                        cursor: 'pointer',
                        color: anchor ? 'primary.main' : 'text.disabled',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                    }}
                >
                    <DataObjectIcon sx={{ fontSize: 13 }} />
                </Box>
            </Tooltip>

            <Popper
                open={Boolean(anchor)}
                anchorEl={anchor}
                placement="left"
                modifiers={[
                    { name: 'offset', options: { offset: [0, 8] } },
                    { name: 'preventOverflow', options: { padding: 8 } },
                    { name: 'flip', options: { padding: 8 } },
                ]}
                sx={{ zIndex: theme.zIndex.modal + depth }}
            >
                <ClickAwayListener onClickAway={() => pinned && close()}>
                    <Box
                        onMouseEnter={() => {
                            clearTimers();
                            bringToFront();
                        }}
                        onMouseLeave={scheduleClose}
                        onMouseDown={bringToFront}
                        onClick={e => e.stopPropagation()}
                        sx={{
                            position: 'relative',
                            width: 620,
                            maxHeight: 520,
                            display: 'flex',
                            flexDirection: 'column',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: '4px',
                            bgcolor: 'background.paper',
                            boxShadow: 6,
                        }}
                    >
                        <Box
                            sx={{
                                px: 1.25,
                                py: 0.75,
                                borderBottom: 1,
                                borderColor: 'divider',
                                bgcolor: alpha(theme.palette.text.primary, 0.03),
                            }}
                        >
                            <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>
                                Raw market data
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    color: 'text.disabled',
                                    fontFamily: 'monospace',
                                }}
                            >
                                {marketId}
                            </Typography>
                        </Box>

                        <Box sx={{ overflow: 'auto', p: 1.25, pb: 5 }}>
                            {loading && (
                                <>
                                    <Skeleton height={14} />
                                    <Skeleton height={14} />
                                    <Skeleton height={14} width="70%" />
                                </>
                            )}

                            {error && (
                                <Typography
                                    sx={{ fontSize: '11px', color: 'text.disabled' }}
                                >
                                    Could not load the raw data.
                                </Typography>
                            )}

                            {data?.description && (
                                <Box sx={{ mb: 1.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            color: 'text.disabled',
                                            mb: 0.25,
                                        }}
                                    >
                                        Description
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: 1.45,
                                        }}
                                    >
                                        {data.description}
                                    </Typography>
                                </Box>
                            )}

                            {data && (
                                <Box
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        p: 1,
                                        borderRadius: '4px',
                                        border: 1,
                                        borderColor: 'divider',
                                        bgcolor: alpha(theme.palette.text.primary, 0.04),
                                        overflow: 'auto',
                                    }}
                                >
                                    <Box
                                        component="code"
                                        className="language-json"
                                        sx={{
                                            fontSize: '10px',
                                            fontFamily: 'monospace',
                                            lineHeight: 1.5,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {rawText}
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        <Box
                            onClick={copy}
                            sx={{
                                position: 'absolute',
                                left: 10,
                                bottom: 10,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: '3px',
                                border: 1,
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                boxShadow: 2,
                                cursor: rawText ? 'pointer' : 'default',
                                opacity: rawText ? 1 : 0.5,
                                color: copied ? 'primary.main' : 'text.secondary',
                                '&:hover': rawText
                                    ? { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                                    : undefined,
                            }}
                        >
                            {copied ? (
                                <CheckIcon sx={{ fontSize: 12 }} />
                            ) : (
                                <ContentCopyIcon sx={{ fontSize: 12 }} />
                            )}
                            <Typography sx={{ fontSize: '10px', color: 'inherit' }}>
                                {copied ? 'Copied' : 'Copy JSON'}
                            </Typography>
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popper>
        </>
    );
}